import React, {useState} from "react";
import {useNavigate} from "react-router-dom";
import _http_service from "../../../../../services/HttpService";
import _image_service from "../../../../../services/ImageService";
import ChooseYourStarterState from "../../../../../model/pokemon/ChooseYourStarterState";
import Modal from "../../../../../components/modal/Modal";
import Pokemon from "../../../../../model/pokemon/Pokemon";
import Slider from "../../../../../components/slider/Slider";

export interface ChooseYourStarterGameProps {
    gameState: ChooseYourStarterState;
    setGameState: (gameState: ChooseYourStarterState) => void;
    error?: string;
    showError: (show?: boolean) => void;
}

export const _unknown_pokemon: Pokemon = {
    id: 'uk',
    name: 'Unknown',
    shiny: false
};

const ChooseYourStarterGame = (props: ChooseYourStarterGameProps) => {

    const _sparkle_emoji: string = '✨';

    const [info, setInfo] = useState<string | undefined>();
    const [isConfirm, setConfirm] = useState<boolean>(true);

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
    const [currentPokemon, setCurrentPokemon] = useState<Pokemon>(_unknown_pokemon);

    const navigateTo = useNavigate();

    const selectPokemon = (pokemon: Pokemon): void => {
        setCurrentPokemon(pokemon);
        if (isConfirm) {
            setModalIsOpen(true);
        } else {
            confirmSelection(pokemon);
        }
    }

    const confirmSelection = (pokemon: Pokemon): void => {
        setModalIsOpen(false);

        let pokemonId: string = pokemon.id;
        if (props.gameState.selectedPokemon.length === props.gameState.teamSize! - 1) {
            pokemon.id = pokemonId;
            props.setGameState({...props.gameState, selectedPokemon: [...props.gameState.selectedPokemon, pokemon]});
            setInfo('Compiling your results!');
        }
        _http_service.selectChooseYourStarterGamePokemon(props.gameState.id, pokemonId)
            .then(response => {
                props.setGameState(response);
                if (response.selectedPokemon.length === response.teamSize) {
                    navigateTo(`/choose-your-starter/result?id=${props.gameState.id}`, {replace: true});
                }
            })
            .catch(() => {
                props.showError();
            });
    }

    const endGame = (): void => {
        _http_service.endChooseYourStarterGame(props.gameState.id)
            .then(() => {
                navigateTo('/choose-your-starter');
            })
            .catch(() => {
                props.showError();
            });
    }

    const shortenName = (name: string): string => {
        let shortName: string = name;
        let index: number = name.indexOf('(');
        if (index >= 0) {
            shortName = shortName.substring(0, index);
        }
        if (shortName.startsWith("Gigantamax")) {
            shortName = "G-max " + shortName.substring(11);
        }

        return shortName;
    }

    return <>
        <Modal className="cys-modal"
               data-testid="confirmation-modal"
               isOpen={modalIsOpen}
               onClose={() => setModalIsOpen(false)}>
            <div className="cys-modal-view">
                <p className="center">Do you want the Pokemon {currentPokemon.name}?</p>
                <div className="cys-modal-view-buttons">
                    <button onClick={() => confirmSelection(currentPokemon)}>Yes</button>
                    <button onClick={() => setModalIsOpen(false)}>No</button>
                </div>
            </div>
        </Modal>
        <p className="center">Select one of these Pokemon to add to your team!</p>
        <div className="center cys-confirm-slider">
            <span className="center">Confirm selection: </span>
            <Slider data-testid="confirmation-slider"
                    isOn={isConfirm}
                    onChange={(value) => setConfirm(value)}/>
        </div>
        <p className="center cys-message">
            &nbsp;
            {info && <><span className="cys-info center">{info}</span><br/></>}
            {props.error && <span className="cys-error center">{props.error}</span>}
        </p>
        <div className="cys-pokemon-selection">
            <ul className="cys-pokemon-list">
                <li>
                    <button className="cys-pokemon-option"
                            disabled={props.gameState.left.id === 'uk'}
                            onClick={() => selectPokemon(props.gameState.left)}>
                        <img className="cys-pokemon"
                             src={_image_service.getCdnImageSrc(`common/pokemon/${props.gameState.left.id}.png`, "thumbnail")}
                             alt={(props.gameState.left.shiny ? "Shiny " : "") + props.gameState.left.name}
                             title={`Tap here to select ${(props.gameState.left.shiny ? "Shiny " : "") + props.gameState.left.name}`}/>
                        <span>{(props.gameState.left.shiny ? _sparkle_emoji : "")}</span>
                    </button>
                    <button className="cys-pokemon-option-pokeball"
                            disabled={props.gameState.left.id === 'uk'}
                            onClick={() => selectPokemon(props.gameState.left)}>
                        <img className="cys-pokemon-pokeball"
                             src={_image_service.getCdnImageSrc(`chase/assets/choose-your-starter/professor_oaks_pokeball.png`, "thumbnail")}
                             alt="Left Pokeball"
                             title={`Tap here to select ${(props.gameState.left.shiny ? "Shiny " : "") + props.gameState.left.name}`}/>
                    </button>
                </li>
                <li>
                    <button className="cys-pokemon-option"
                            disabled={props.gameState.center.id === 'uk'}
                            onClick={() => selectPokemon(props.gameState.center)}>
                        <img className="cys-pokemon"
                             src={_image_service.getCdnImageSrc(`common/pokemon/${props.gameState.center.id}.png`, "thumbnail")}
                             alt={(props.gameState.center.shiny ? "Shiny " : '') + props.gameState.center.name}
                             title={`Tap here to select ${(props.gameState.center.shiny ?  "Shiny " : '') + props.gameState.center.name}`}/>
                        <span>{(props.gameState.center.shiny ? _sparkle_emoji : "")}</span>
                    </button>
                    <button className="cys-pokemon-option-pokeball"
                            disabled={props.gameState.center.id === 'uk'}
                            onClick={() => selectPokemon(props.gameState.center)}>
                        <img className="cys-pokemon-pokeball"
                             src={_image_service.getCdnImageSrc(`chase/assets/choose-your-starter/professor_oaks_pokeball.png`, "thumbnail")}
                             alt="Center Pokeball"
                             title={`Tap here to select ${(props.gameState.center.shiny ?  "Shiny " : '') + props.gameState.center.name}`}/>
                    </button>
                </li>
                <li>
                    <button className="cys-pokemon-option"
                            disabled={props.gameState.right.id === 'uk'}
                            onClick={() => selectPokemon(props.gameState.right)}>
                        <img className="cys-pokemon"
                             src={_image_service.getCdnImageSrc(`common/pokemon/${props.gameState.right.id}.png`, "thumbnail")}
                             alt={(props.gameState.right.shiny ? "Shiny " : '') + props.gameState.right.name}
                             title={`Tap here to select ${(props.gameState.right.shiny ? "Shiny " : '') + props.gameState.right.name}`}/>
                        <span>{(props.gameState.right.shiny ? _sparkle_emoji : "")}</span>
                    </button>
                    <button className="cys-pokemon-option-pokeball"
                            disabled={props.gameState.right.id === 'uk'}
                            onClick={() => selectPokemon(props.gameState.right)}>
                        <img className="cys-pokemon-pokeball"
                             src={_image_service.getCdnImageSrc(`chase/assets/choose-your-starter/professor_oaks_pokeball.png`, "thumbnail")}
                             alt="Right Pokeball"
                             title={`Tap here to select ${(props.gameState.right.shiny ? "Shiny " : '') + props.gameState.right.name}`}/>
                    </button>
                </li>
            </ul>
        </div>
        <ul className="cys-team-list">
            {props.gameState.selectedPokemon.concat([...Array(6 - props.gameState.selectedPokemon.length)]
                .fill(_unknown_pokemon, 0, 6)).map((pokemon, index) =>
                <li key={`team-member-${pokemon.id}-${index}`}>
                    <div>
                        <img className="cys-pokemon"
                             src={_image_service.getCdnImageSrc(`common/pokemon/${pokemon.id}.png`, "thumbnail")}
                             alt={pokemon.name}
                             title={pokemon.id === 'uk'
                                 ? 'Waiting for your selection!'
                                 : `You selected ${pokemon.shiny ? 'shiny ' : ''}${pokemon.name}`}/>
                        <span className="shiny-mark">{(pokemon.shiny ? _sparkle_emoji : "")}</span>
                    </div>
                    <span className="cys-pokemon-team-name">{pokemon.id !== 'uk' && shortenName(pokemon.name)}</span>
                </li>
            )}
        </ul>
        <button className="cys-end-game center"
                onClick={() => endGame()}>
            Click here to end the game without filling out your full team
        </button>
    </>;
}

export default ChooseYourStarterGame;